import React from "react";
import clsx from "clsx";

import { ReactComponent as Logo } from "../../../../assets/svg/logo.svg";
import { ReactComponent as EyeOpen } from "../../../../assets/svg/eye_open.svg";
import { ReactComponent as EyeClose } from "../../../../assets/svg/eye_close.svg";
import { ReactComponent as GoogleIcon } from "../../../../assets/svg/google.svg";
import { ReactComponent as MicrosoftIcon } from "../../../../assets/svg/microsoft.svg";
import { API_URL } from "../../../../studentStore";
import styles from "../Login.module.scss";

type Props = {
  calling: boolean;
  login: (identity: string, password: string) => void;
  redirectPath?: string;
};

const validates: { [key: string]: (value: any) => string } = {};

validates["identity"] = val => {
  return val.identity ? "" : "この項目は入力が必須です。";
};

validates["password"] = val => {
  return !val.password.trim()
    ? "この項目は入力が必須です。"
    : val.password.length < 6 || val.password.length > 32
    ? "パスワードは6文字以上32文字以内で入力してください。"
    : "";
};

export const LoginForm = (props: Props) => {
  const [isShowPassword, setIsShowPassword] = React.useState(false);
  const redirectPathQuery = props.redirectPath
    ? `&redirect_path=${encodeURIComponent(props.redirectPath)}`
    : "";
  const [state, setState] = React.useState({
    identity: "",
    password: "",
    errorMessages: {} as { [key: string]: string },
  });

  const onSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const errorMessages = {} as { [key: string]: string };
      Object.keys(validates).forEach(key => {
        const ret = validates[key](state);
        if (ret) {
          errorMessages[key] = ret;
        }
      });
      if (Object.keys(errorMessages).length > 0) {
        setState(s => ({ ...s, errorMessages }));
        return;
      }
      props.login(state.identity, state.password);
    },
    [state, props],
  );

  const onChange = React.useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      e.preventDefault();
      const val = e.currentTarget.value;
      const key = e.currentTarget.name;
      const args = { [key]: val };
      const ret = validates[key](args);
      setState(prevState => ({
        ...prevState,
        [key]: val,
        errorMessages: { ...prevState.errorMessages, [key]: ret },
      }));
    },
    [],
  );

  return (
    <div className={styles.wrapper}>
      <Logo className={styles.logo} />
      <form className={styles.content} onSubmit={onSubmit}>
        <div className={styles.title}>
          <p className={clsx(styles.primary, styles.en)}>LOGIN</p>
          <p className={styles.secondary}>生徒</p>
        </div>
        <div className={styles.inputField}>
          <fieldset>
            <input
              type="text"
              name="identity"
              className={state.errorMessages["identity"] ? styles.isError : ""}
              id="identity"
              value={state.identity}
              onChange={onChange}
              onBlur={onChange}
              placeholder="ログインIDを入力"
            />
            {state.errorMessages["identity"] && (
              <p className={styles.errorMessage}>
                {state.errorMessages["identity"]}
              </p>
            )}
          </fieldset>
          <fieldset>
            <div className={styles.inputWrapper}>
              <input
                type={isShowPassword ? "text" : "password"}
                id="password"
                name="password"
                className={
                  state.errorMessages["password"] ? styles.isError : ""
                }
                value={state.password}
                onChange={onChange}
                onBlur={onChange}
                placeholder="パスワードを入力"
              />
              <span
                className={styles.eyeIcon}
                onClick={() => {
                  setIsShowPassword(!isShowPassword);
                }}
              >
                {isShowPassword ? <EyeOpen /> : <EyeClose />}
              </span>
            </div>
            {state.errorMessages["password"] && (
              <p className={styles.errorMessage}>
                {state.errorMessages["password"]}
              </p>
            )}
          </fieldset>
          <button
            className={styles.submitButton}
            type="submit"
            disabled={
              Object.keys(state.errorMessages).filter(
                k => state.errorMessages[k],
              ).length > 0 || props.calling
            }
          >
            ログイン
          </button>
        </div>
        <div className={styles.separator}>または</div>
        <div className={styles.socialLogin}>
          <button
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = `${API_URL?.replace(
                "/api/v1/student",
                "",
              )}/auth/google_oauth2?kind=student${redirectPathQuery}`;
            }}
          >
            <GoogleIcon className={styles.icon} />
            Googleでログイン
          </button>
          <button
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = `${API_URL?.replace(
                "/api/v1/student",
                "",
              )}/auth/azure_activedirectory_v2?kind=student&prompt=select_account${redirectPathQuery}`;
            }}
          >
            <MicrosoftIcon className={styles.icon} />
            Microsoftでログイン
          </button>
        </div>
        <div className={styles.block}>
          <p className={clsx(styles.text, styles.bold)}>
            ログインできないときは以下を確認してください
          </p>
          <ul className={styles.list}>
            <li>全角で入力していませんか？</li>
            <li>入力する内容を間違えていませんか？</li>
          </ul>
        </div>
        <div className={styles.box}>
          <div className={styles.block}>
            <p className={`${styles.text} ${styles.bold} ${styles.textCenter}`}>
              ログインID / パスワードを忘れた場合
            </p>
            <ul className={`${styles.list} ${styles.orderedList}`}>
              <li>先生に[仮パスワード]を再発行してもらってください。</li>
              <li>
                教えてもらった[ログインID]と[仮パスワード]でログインしてください。
              </li>
            </ul>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
